import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import useFetchEvaluacion from '../hooks/UseFetchEvaluacion'
import EvaluacionStepper from './EvaluacionStepper'

const getProgressColor = (value) => {
  if (value >= 75) return 'success'
  if (value >= 33) return 'warning'
  return 'error'
}

const EvaluacionWizard = ({ id, evaluado, evaluador = null, onBack, onBackToEvaluaciones }) => {
  const evaluadorDni = evaluador?.rh_dni ?? null
  const { response: evaluacion, loading, error } = useFetchEvaluacion(id, evaluadorDni)
  const { enqueueSnackbar } = useSnackbar()
  const [savedAnswers, setSavedAnswers] = useState(null)

  const answeredProgress = useMemo(() => {
    if (!Array.isArray(evaluacion) || evaluacion.length === 0) return 0
    const allQuestions = evaluacion.flatMap((cap) => cap.preguntas || [])
    if (allQuestions.length === 0) return 0

    const answered = savedAnswers
      ? savedAnswers.filter(
          (a) =>
            a.valor != null ||
            a.porcentaje != null ||
            (a.observaciones && a.observaciones.trim() !== ''),
        )
      : allQuestions.filter((q) => q.cerrado === 'S' || q.valor != null || q.porcentaje != null)

    return Math.round((answered.length / allQuestions.length) * 100)
  }, [evaluacion, savedAnswers])

  const progressColor = getProgressColor(answeredProgress)
  const [apellidos = '', nombre = ''] = (evaluado?.nombre ?? '').split(', ')
  const [apellidosEval = '', nombreEval = ''] = (evaluador?.nombre ?? '').split(', ')

  if (loading) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={180} height={36} />
          <Skeleton variant="rounded" width={160} height={36} />
        </Box>
        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            <Skeleton variant="circular" width={80} height={80} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="50%" height={28} />
              <Skeleton variant="text" width="35%" height={20} />
              <Skeleton variant="text" width="40%" height={16} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
            <Skeleton variant="circular" width={72} height={72} />
          </Box>
        </Card>
        <Paper sx={{ p: 4 }}>
          <Skeleton variant="text" width="80%" height={40} sx={{ mb: 2 }} />
          {Array.from({ length: 4 }).map((_, index) => (
            <Box key={index} sx={{ mb: 4 }}>
              <Skeleton variant="text" width="70%" height={30} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={50} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="90%" height={30} />
            </Box>
          ))}
        </Paper>
      </Box>
    )
  }

  if (error) {
    enqueueSnackbar(`Error al cargar la evaluación: ${error}`, { variant: 'error' })
    return <Typography>Error: {error}</Typography>
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sección sticky: botones + tarjeta del trabajador */}
      <Box
        sx={{
          position: 'sticky',
          top: '70px',
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pt: 1,
          pb: 1,
        }}
      >
        {/* Botones de navegación */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
            Lista de evaluados
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PeopleAltIcon />}
            onClick={onBackToEvaluaciones}
            size="small"
          >
            Evaluaciones
          </Button>
        </Box>

        {/* Tarjeta del evaluador (solo en modo admin) */}
        {evaluador && (
          <Card
            sx={{
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5 }}>
              <Avatar
                src={`/assets/images/${evaluador.imagen}`}
                sx={{ width: 48, height: 48, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="bold" lineHeight={1.2}>
                  {apellidosEval}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}
                >
                  {nombreEval}
                </Typography>
              </Box>
              <Chip label="Evaluador" color="primary" size="small" />
            </Box>
          </Card>
        )}

        {/* Tarjeta del trabajador */}
        {evaluado && (
          <Card
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <Avatar
                src={`/assets/images/${evaluado?.imagen}`}
                sx={{ width: 80, height: 80, flexShrink: 0 }}
              />

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                  {apellidos}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}
                >
                  {nombre}
                </Typography>
                <Typography
                  variant="overline"
                  display="block"
                  color="text.secondary"
                  lineHeight={1.5}
                  sx={{ mt: 0.5 }}
                >
                  Puesto
                </Typography>
                <Typography variant="body2">
                  {evaluado?.puestos?.[0]?.descripcion ?? evaluado?.puesto ?? '—'}
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={answeredProgress}
                    color={progressColor}
                    size={72}
                    thickness={4}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold" color={`${progressColor}.main`}>
                      {`${answeredProgress}%`}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Completado
                </Typography>
              </Box>
            </Box>
          </Card>
        )}
      </Box>

      {/* Contenido del stepper */}
      <Paper
        sx={{
          p: { xs: 2, md: 0 },
          mt: 1,
          overflow: 'hidden',
          backgroundColor: 'background.default',
        }}
      >
        <EvaluacionStepper
          evaluacion={evaluacion}
          onBack={onBack}
          evaluado={evaluado}
          evaluadorDni={evaluadorDni}
          onAnswerSaved={setSavedAnswers}
        />
      </Paper>
    </Box>
  )
}

export default EvaluacionWizard
